import type { RemoteHostStatus, RemoteSessionStatus } from '../../../api/generated/model'

export type HostPresentation = {
  tone: 'positive' | 'warning' | 'neutral' | 'danger'
  labelKey: 'ready' | 'pending' | 'inUse' | 'checking' | 'offline' | 'disabled'
  action: 'connect' | 'connecting' | 'approve' | 'none'
}

function unreachable(value: never): never {
  throw new Error(`Unhandled remote state: ${String(value)}`)
}

export function getHostPresentation(
  status: RemoteHostStatus,
  canConnect: boolean,
  hasUnresolvedAttempt: boolean,
): HostPresentation {
  if (status === 'ready') {
    return {
      tone: 'positive',
      labelKey: 'ready',
      action: hasUnresolvedAttempt ? 'connecting' : canConnect ? 'connect' : 'none',
    }
  }
  // Discovered on the gateway, waiting on a person. The action offered is the
  // decision itself, not a connection: approving admits the server, and who may
  // use it is still assigned separately.
  if (status === 'pending') return { tone: 'neutral', labelKey: 'pending', action: 'approve' }
  if (status === 'in_use') return { tone: 'warning', labelKey: 'inUse', action: 'none' }
  if (status === 'checking') return { tone: 'neutral', labelKey: 'checking', action: 'none' }
  if (status === 'offline') return { tone: 'danger', labelKey: 'offline', action: 'none' }
  if (status === 'disabled') return { tone: 'neutral', labelKey: 'disabled', action: 'none' }
  return unreachable(status)
}

export function isSessionPollingStatus(status: RemoteSessionStatus): boolean {
  if (status === 'reserved' || status === 'connecting' || status === 'active' || status === 'status_unknown' || status === 'closing') {
    return true
  }
  if (status === 'closed' || status === 'failed') return false
  return unreachable(status)
}

export function isSessionTerminalStatus(status: RemoteSessionStatus): boolean {
  if (status === 'closed' || status === 'failed') return true
  if (status === 'reserved' || status === 'connecting' || status === 'active' || status === 'status_unknown' || status === 'closing') {
    return false
  }
  return unreachable(status)
}
